<template lang="pug">
.dashboard-wrapper
  b-row
    b-col
      b-card(no-body, class="pt-2 px-2")
        filters(
          :zoneFilterShow="zoneAgencyFilter"
          :agencyFilterShow="zoneAgencyFilter"
          :locationFilterShow="true",
          :roleFilterShow="true",
          :processFilterShow="true",
          :workerFilterShow="true",
          :observerFilterShow="true",
          :dateRangeFilterShow="true",
          :processTypeFilterShow="true",
          @changeFilter="updateFilter"
        )

  b-row(v-if="isLoading")
    b-col
      b-card(class="py-5")
        loading(
          :active="true" 
          :is-full-page="false"
          color="#498ceb"
        )
        
  div(v-else)
    b-row(v-if="!confirmations_total")
      b-col
        b-card(class="text-center")
          h5(class="mb-0")
            | {{ $t('message.no_confirmations_found') }}

    div(v-else)
      b-row
        b-col(sm="3")
          statistic-card-vertical-total(:statistic="behaviours_total")
        b-col(sm="3")
          statistic-card-vertical-index(:statistic="adherence_index")
        b-col(sm="3")
          statistic-card-vertical-confirmations(:statistic="confirmations_total")

      //- b-card
        b-table-simple(hover, small, caption-top, responsive)
          b-thead(head-variant="light")
            b-tr
              th {{ $t('message.tableHeader.behaviour') }}
              th {{ $t('message.tableHeader.activity') }}
              th {{ $t('message.tableHeader.process') }}
              th {{ $t('message.tableHeader.score') }}
              th {{ $t('message.tableHeader.total') }}
              th {{ $t('message.tableHeader.adherence') }}
            b-tr(v-for="(behaviour, index) in behaviours", :key="index")
              b-td(v-html="behaviour.behaviour", class="behaviourClass")
              b-td {{ behaviour.activity }}
              b-td {{ behaviour.process }}
              b-td {{ behaviour.score }}
              b-td {{ behaviour.total }}
              b-td {{ behaviour.adherenceText }}

      b-card
        b-table(
          :items="behaviours"
          :fields="tableHeaders"
          hover
          responsive
        )
          template(#cell(adherence)="data")
            span {{ data.item.adherenceText }}

      b-button(v-if="hasPermission('adherence-behaviours', 'export')", :disabled="!behaviours.length")
        json-excel(:data="behaviours", :fields="excelFields")
          | {{ $t('download_xlsx') }}
</template>

<script>
import { onMounted, ref, computed, watch } from "@vue/composition-api/dist/vue-composition-api";
import useNotifications from "@/composables/useNotifications";
import { BTable, BTableSimple, BThead, BTr, BTd, BCard } from "bootstrap-vue";
import JsonExcel from "vue-json-excel";
import StatisticCardVerticalTotal from './charts/StatisticCardVerticalTotal.vue'
import StatisticCardVerticalIndex from './charts/StatisticCardVerticalIndex.vue'
import StatisticCardVerticalConfirmations from './charts/StatisticCardVerticalConfirmations.vue'
import Filters from "@/views/organization/Filters.vue";
import i18n from '@/libs/i18n'
import ViewButton from '@/views/components/Shared/Buttons/ViewButton.vue'
import { useRouter } from '@core/utils/utils'
import useCommonDashboards from '@/views/habit/useCommonDashboards'
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';
import store from "@/store";
import useCommon from '@/views/organization/useCommon'
import { useLambda } from '@/views/habit/awsLambda'
import { oid, date } from '@/helpers/mongoHelpers'
import { hasPermission, permissionScope } from '@/composables/usePermission'

export default {
  components: {
    BTable,
    BTableSimple,
    BThead,
    BTr,
    BTd,
    BCard,
    JsonExcel,
    StatisticCardVerticalTotal,
    StatisticCardVerticalIndex,
    StatisticCardVerticalConfirmations,
    Filters,
    ViewButton,
    Loading,
  },
  setup() {
    const { showErrorMessage } = useNotifications();
    const { handleError } = useCommon()
    const { getItems, getItemsAggregate } = useLambda()
    
    const userData = store.state?.userStore?.userData;
    const userObjectId = oid(userData.worker_id.$oid)
    const userRole = userData.role
    const clientId = userData.role !== "admin" ? userData.client.$oid : null;
    const userLocations = userData.locations?.length ? userData.locations : null;
    const { default_language } = JSON.parse(localStorage.getItem('clientData') || '{}')
    const behaviours = ref([]);
    const behaviours_total = ref(0);
    const confirmations_total = ref(0);
    const adherence_index = ref("-");
    const locationFilter = ref([]);
    const roleFilter = ref([]);
    const processFilter = computed(() => store.state.filtersStore.processFilter);
    const companyFilter = computed(() => store.state.filtersStore.companyFilter)
    const idFilter = computed(() => store.state.filtersStore.idFilter)
    const workerFilter = ref([]);
    const observerFilter = ref([]);
    const zoneFilter = ref([]);
    const agencyFilter = ref([]);
    const now = new Date()
    const currentMonth = now.getMonth()
    const currentYear = now.getFullYear()
    const currentDay = now.getDate()
    const lastDayOfMonthDate = new Date(currentYear, currentMonth + 1, 0)
    const lastDayOfMonthNumber = lastDayOfMonthDate.getDate()
    const thirtyDaysBeforeDate = new Date(currentYear, currentMonth, currentDay - 30)
    const dateRangeFilter = default_language === "en"
      ? ref(`${thirtyDaysBeforeDate.getMonth() + 1}-${thirtyDaysBeforeDate.getDate()}-${thirtyDaysBeforeDate.getFullYear()} to ${currentMonth + 1}-${currentDay}-${currentYear}`)
      : ref(`${thirtyDaysBeforeDate.getDate()}-${thirtyDaysBeforeDate.getMonth() + 1}-${thirtyDaysBeforeDate.getFullYear()} to ${currentDay}-${currentMonth + 1}-${currentYear}`)
    const isLoading = ref(true)
    const zoneAgencyFilter = localStorage.getItem("clientData")
      ? JSON.parse(localStorage.getItem("clientData")).zone_agency_filter
      : false
    const excelFields = ref({
      [i18n.t('message.tableHeader.behaviour')]: "behaviour",
      [i18n.t('message.tableHeader.score')]: "score",
      [i18n.t('message.tableHeader.total')]: "total",
      [i18n.t('message.tableHeader.adherence')]: "adherence",
    })
    const processTypeFilter = ref([])

    // Table Headers
    const tableHeaders = computed(() => {
      return [
        {
          key: 'behaviour',
          label: i18n.t('message.tableHeader.behaviour'),
          sortable: true,
        },
        {
          key: 'score',
          label: i18n.t('message.tableHeader.score'),
          sortable: true,
        },
        {
          key: 'total',
          label: i18n.t('message.tableHeader.total'),
          sortable: true,
        },
        {
          key: 'adherence',
          label: i18n.t('message.tableHeader.adherence'),
          sortable: true,
        },
      ]
    })

    const { router } = useRouter()
    const { getDatesFromRange } = useCommonDashboards()

    const getBehaviours = async () => {
      isLoading.value = true

      try {
        const query = {
          deleted: { $ne: true }
        }
        if (clientId) query.client_id = oid(clientId)

        const items = await getItems({ collection: 'behaviour', query })

        behaviours.value = items?.map(e => ({ behaviour: e.name?.replace(/<[^>]*>/g, '') })) || []
        behaviours_total.value = items?.length || 0

        getConfirmations()
      } catch (error) {
        console.log(error)
        showErrorMessage(i18n.t('message.data_fetch_error'))
        isLoading.value = false
      }
    }

    async function getConfirmations() {
      isLoading.value = true
      const [startFilter, endFilter] = getDatesFromRange(dateRangeFilter.value)

      try {
        const initialQuery = {
          pending: { $ne: true },
          date: { $gte: date(startFilter), $lt: date(endFilter) },
        }
        if (clientId) initialQuery.client_id = oid(clientId)

        const finalQuery = {}

        if (observerFilter.value.length > 0) {
          finalQuery["supervisor._id"] = { $in: observerFilter.value }
        }

        if (workerFilter.value.length > 0) {
          finalQuery["worker._id"] = { $in: workerFilter.value }
        }

        if (processFilter.value.length > 0) {
          finalQuery["process._id"] = { $in: processFilter.value }
        }

        if (locationFilter.value.length > 0) {
          finalQuery["worker.locations._id"] = { $in: locationFilter.value }
        }

        if (roleFilter.value.length > 0) {
          finalQuery["worker.roles"] = { $in: roleFilter.value.map(e => oid(e)) }
        }

        if (companyFilter.value.length > 0) {
          finalQuery['process.company'] = { $in: companyFilter.value.map(e => oid(e)) }
        }
  
        if (idFilter.value.length > 0) {
          finalQuery['process.id'] = { $in: idFilter.value.map(e => oid(e)) }
        }

        const conditions = []

        // Process type filter
        if (processTypeFilter.value.length) {
          conditions.push({
            $or: processTypeFilter.value.map(codePrefix => ({
              'process.code': { $regex: `^${codePrefix}` }
            }))
          })
        }

        // Privileges filter
        if (permissionScope('adherence-behaviours', 'view') === 'own') {
          if (userRole === 'employee') {
            conditions.push({
              $or: [
                { 'process.evaluator': userObjectId },
              ]
            })
          } else {
            conditions.push({
              $or: [
                { 'process.responsibles': userObjectId },
                { 'process.leadResponsibles': userObjectId },
                { 'process.evaluator': userObjectId },
              ]
            })
          }
        }

        // Armar el match final
        if (conditions.length) {
          finalQuery.$and = conditions
        }

        const pipeline = [
          { $match: initialQuery },
          { $lookup: { from: 'worker', localField: 'worker', foreignField: '_id', as: 'worker', pipeline: [ { $project: { deleted: 1, supervisors: 1, name: 1, locations: 1, roles: 1 } }, { $addFields: { _id: { $toString: "$_id" } } }, { $lookup: { from: 'worker', localField: 'supervisors', foreignField: '_id', as: 'supervisors', pipeline: [ { $project: { name: 1, locations: 1 } }, { $addFields: { _id: { $toString: "$_id" } } }, { $lookup: { from: 'location', localField: 'locations', foreignField: '_id', as: 'locations', pipeline: [ { $project: { location: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ] } } ] } }, { $lookup: { from: 'location', localField: 'locations', foreignField: '_id', as: 'locations', pipeline: [ { $project: { location: 1, zone: 1, agency: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ] } } ] } },
          { $lookup: { from: 'worker', localField: 'supervisor', foreignField: '_id', as: 'supervisor', pipeline: [ { $project: { name: 1, locations: 1 } }, { $addFields: { _id: { $toString: "$_id" } } }, { $lookup: { from: 'location', localField: 'locations', foreignField: '_id', as: 'locations', pipeline: [ { $project: { location: 1, zone: 1, agency: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ] } } ] } },
          { $lookup: { from: 'process', localField: 'process', foreignField: '_id', as: 'process', pipeline: [ { $project: { name: 1, code: 1, responsibles: 1, leadResponsibles: 1, evaluator: 1, company: 1, id: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ] } },
          { $addFields: { worker: { $arrayElemAt: ["$worker", 0] }, supervisor: { $arrayElemAt: ["$supervisor", 0] }, process: { $arrayElemAt: ["$process", 0] } } },
          { $match: finalQuery },
          { $addFields: { _id: { $toString: "$_id" } } },
        ]

        const items = await getItemsAggregate({ collection: 'confirmation', pipeline })

        parseConfirmationData(items)
      } catch (error) {
        console.log(error)
        handleError({ error, defaultMessage: i18n.t('message.err_confirmation_list') })
      } finally {
        isLoading.value = false
      }
    }

    function parseConfirmationData(items) {
      // Reset values for each behaviour
      behaviours.value.forEach(e => {
        e.score = "-"
        e.total = "-"
        e.adherence = "-"
        e.adherenceText = "-"
      })

      confirmations_total.value = items?.length || 0
      let adherence_data = {score: 0, total: 0}

      for (const confirmation of items) {
        for (const activity of confirmation.activities) {
          for (const behaviour of activity.behaviours) {
            if ((behaviour.type === "bool" || behaviour.type === "rating") && behaviour.answer && behaviour.answer !== "unknown" && behaviour.answer !== "N/A") {
              for (const b of behaviours.value) {
                if (behaviour.name?.replace(/<[^>]*>/g, '') !== b.behaviour) continue

                if (behaviour.type === "bool") {
                  if (behaviour.answer === "true" && behaviour.expectedAnswer || behaviour.answer === "false" && !behaviour.expectedAnswer) {
                    b.score = b.score === "-" ? 1 : b.score + 1
                    adherence_data.score += 1
                  }
                  if (behaviour.answer === "true" || behaviour.answer === "false") {
                    if (b.score === "-") b.score = 0
                    b.total = b.total === "-" ? 1 : b.total + 1
                    adherence_data.total += 1
                  }
                } else {
                  b.score = b.score === "-" ? parseInt(behaviour.answer) : b.score + parseInt(behaviour.answer)
                  b.total = b.total === "-" ? 5 : b.total + 5
                  adherence_data.score += parseInt(behaviour.answer)
                  adherence_data.total += 5
                }
                if (b.total !== "-") {
                  b.adherence = behaviourAdherence(b.score, b.total)
                  b.adherenceText = `${b.adherence}%`
                }
              }
            }
          }
        }
      }

      behaviours.value.sort(function (a, b) {
        if (a.adherence === b.adherence) return b.total - a.total
        if (b.adherence === "-" && a.adherence !== "-") return -1
        return a.adherence - b.adherence
      })

      adherence_index.value = adherence_data.total === 0
        ? "-"
        : `${behaviourAdherence(adherence_data.score, adherence_data.total)}%`
    }

    function behaviourAdherence(score, total) {
      return Math.round(score / total * 1000) / 10
    }

    function updateFilter(data) {
      locationFilter.value = data.locationFilter;
      roleFilter.value = data.roleFilter;
      // processFilter.value = data.processFilter; // processFilter ya se actualiza automáticamente desde el store global
      workerFilter.value = data.workerFilter;
      observerFilter.value = data.observerFilter;
      dateRangeFilter.value = data.dateRangeFilter;
      zoneFilter.value = data.zoneFilter;
      agencyFilter.value = data.agencyFilter;
      processTypeFilter.value = data.processTypeFilter

      getConfirmations();
    }

    watch([companyFilter, idFilter], () => {
      getConfirmations()
    })

    onMounted(getBehaviours);

    return {
      hasPermission,
      updateFilter,
      behaviours,
      behaviours_total,
      confirmations_total,
      adherence_index,
      excelFields,
      router,
      isLoading,
      zoneAgencyFilter,
      tableHeaders,
    };
  },
};
</script>

<style scoped>
  .behaviourClass ::v-deep p {
    margin: 0px;
  }
</style>